import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Paper,
  Stack,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import useStatisticJob from '../../hooks/useStatisticJob';
import useStatisticCacheEntries from '../../hooks/useStatisticCacheEntries';
import StatisticViewShell from '../ui/StatisticViewShell';
import StatisticMatrixTables from '../ui/StatisticMatrixTables';
import { percentFormatter } from '../../util/formatHelper';
import { STATISTIC_TYPE_CALL_CENTER } from '../../constants/statisticTypes';
import {
  getBarTooltipCursor,
  getCartesianGridStroke,
  getRechartsAxisStroke,
  getRechartsTooltipProps,
  getStatisticsPlainAccordionSx,
  getStatisticsSurfaceBackground,
  getStatisticsTableRowBorderTop,
  getStatisticsKpiLabelColor,
  getStatisticsKpiHelperColor,
  getRechartsLegendBarProps,
  getRechartsLegendPieProps,
  getStatisticsHtmlThStyle,
  getStatisticsPageRootStyle,
} from '../../util/chartTheme';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
  BarChart,
  Bar,
  ComposedChart,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

const defaultStartDate = dayjs().startOf('month').format('YYYY-MM-DD');
const defaultEndDate = dayjs().endOf('month').format('YYYY-MM-DD');

const CALL_VOLUME_METRICS = [
  { key: 'callsReceived', label: 'Eingegangene Anrufe' },
  { key: 'callsReceivedUnique', label: 'Eingegangene Anrufe (unique)' },
  { key: 'callbacksOpen', label: 'Offene Rückrufe (Tagesende)' },
  { key: 'callbackCount', label: 'Durchgeführte Rückrufe' },
  { key: 'callsDirectlyAnswered', label: 'Direkt beantwortete Anrufe' },
  { key: 'callsDirectlyAnsweredQuote', label: 'Anteil direkt beantwortete Anrufe', unit: '%', isAverage: true },
  { key: 'callsDurationAverage', label: 'Durchschnittliche Anrufdauer', unit: 'sec', isAverage: true },
  { key: 'callsWaittimeAverage', label: 'Durchschnittliche Wartedauer', unit: 'sec', isAverage: true },
];

const CallCenterStatistics = () => {
  const theme = useTheme();
  const chartGridStroke = useMemo(() => getCartesianGridStroke(theme), [theme]);
  const statSurfaceBg = useMemo(() => getStatisticsSurfaceBackground(theme), [theme]);
  const axisStroke = useMemo(() => getRechartsAxisStroke(theme), [theme]);
  const chartLegendBarProps = useMemo(() => getRechartsLegendBarProps(theme), [theme]);
  const chartLegendPieProps = useMemo(() => getRechartsLegendPieProps(theme), [theme]);
  const htmlThStyle = useCallback((extra = {}) => getStatisticsHtmlThStyle(theme, extra), [theme]);
  const chartTooltipProps = useMemo(() => getRechartsTooltipProps(theme), [theme]);
  const plainAccordionSx = useMemo(() => getStatisticsPlainAccordionSx(theme), [theme]);
  const barTooltipCursor = useMemo(() => getBarTooltipCursor(theme), [theme]);
  const tableRowBorderTop = useMemo(() => getStatisticsTableRowBorderTop(theme), [theme]);
  const kpiLabelColor = useMemo(() => getStatisticsKpiLabelColor(theme), [theme]);
  const kpiHelperColor = useMemo(() => getStatisticsKpiHelperColor(theme), [theme]);
  const chartPaletteColors = useMemo(
    () => [
      theme.palette.success.main,
      theme.palette.primary.main,
      theme.palette.warning.main,
      theme.palette.secondary.main,
      theme.palette.error.main,
      theme.palette.info.main,
      theme.palette.grey[700],
      theme.palette.grey[600],
      theme.palette.secondary.dark,
      theme.palette.primary.dark,
    ],
    [theme]
  );
  const activityLineColors = useMemo(
    () => ({
      changes: theme.palette.warning.main,
      bookings: theme.palette.success.main,
      hourly: theme.palette.primary.main,
    }),
    [theme]
  );
  const [startDate, setStartDate] = useState(dayjs(defaultStartDate));
  const [endDate, setEndDate] = useState(dayjs(defaultEndDate));
  const [appliedFilter, setAppliedFilter] = useState({
    startDate: defaultStartDate,
    endDate: defaultEndDate,
  });
  const [chartType, setChartType] = useState('line');
  const [selectedEntryId, setSelectedEntryId] = useState(null);

  const filterParams = useMemo(
    () => ({
      startDate: appliedFilter.startDate,
      endDate: appliedFilter.endDate,
      branchScope: 'all',
    }),
    [appliedFilter]
  );

  const {
    payload: stats,
    isStale,
    cacheComputedAt,
    cacheExpiresAt,
    status,
    job,
    error,
    loadCacheEntry,
    refresh: refreshStatistic,
  } = useStatisticJob({
    statisticType: STATISTIC_TYPE_CALL_CENTER,
    endpoint: '/api/v1/statistic/callCenter',
    filterParams,
  });

  const { entries: cacheEntries, isLoading: entriesLoading } = useStatisticCacheEntries({
    statisticType: STATISTIC_TYPE_CALL_CENTER,
  });

  useEffect(() => {
    if (status === 'failed' && error) toast.error(error);
  }, [status, error]);

  const handleSelectEntry = useCallback(
    entryId => {
      setSelectedEntryId(entryId);
      if (entryId) loadCacheEntry(entryId);
    },
    [loadCacheEntry]
  );

  const isFetching = status === 'fetching' || status === 'pending' || status === 'running';

  const applyFilters = useCallback(
    (nextStart, nextEnd) => {
      const s = nextStart || startDate;
      const e = nextEnd || endDate;
      if (!s || !e || !s.isValid() || !e.isValid()) {
        toast.error('Bitte wähle gültige Daten aus');
        return;
      }
      if (s.isAfter(e)) {
        toast.error('Das Startdatum darf nicht nach dem Enddatum liegen');
        return;
      }
      setSelectedEntryId(null);
      setAppliedFilter({
        startDate: s.format('YYYY-MM-DD'),
        endDate: e.format('YYYY-MM-DD'),
      });
    },
    [startDate, endDate]
  );

  const cards = useMemo(() => {
    if (!stats?.counts) return [];
    const { totalBookings, ccChangedBookings, totalCCChanges, ccUsersActive } = stats.counts;
    const { ccChangePercent } = stats.percentages || {};

    return [
      {
        label: 'Buchungen im Zeitraum',
        value: totalBookings,
        helper: 'Gesamtanzahl der Buchungen',
      },
      {
        label: 'Vom CC bearbeitete Buchungen',
        value: ccChangedBookings,
        helper: `${percentFormatter.format(ccChangePercent || 0)} % aller Buchungen`,
      },
      {
        label: 'Änderungen durch CC',
        value: totalCCChanges,
        helper: 'Gesamtanzahl aller Änderungen',
      },
      {
        label: 'Aktive CC-Mitarbeiter',
        value: ccUsersActive,
        helper: 'Mitarbeiter mit Änderungen',
      },
    ];
  }, [stats]);

  const chartData = useMemo(() => {
    const daily = stats?.timeSeries?.daily || [];
    return daily.map(p => {
      const date = dayjs(p.date);
      return {
        ...p,
        label: date.isValid() ? date.format('DD.MM.') : String(p.date || ''),
      };
    });
  }, [stats]);

  const changeTypePieData = useMemo(() => {
    const types = stats?.changeTypes || [];
    return types
      .slice(0, 10)
      .map(t => ({ name: t.label, value: t.count }))
      .filter(t => t.value > 0);
  }, [stats]);

  const userPieData = useMemo(() => {
    const users = stats?.userStats || [];
    return users
      .slice(0, 8)
      .map(u => ({ name: u.displayName, value: u.changeCount }))
      .filter(u => u.value > 0);
  }, [stats]);

  const hourlyData = useMemo(() => {
    return stats?.hourlyDistribution || [];
  }, [stats]);

  const phoneSummary = stats?.phoneSummary;
  const phoneByDay = useMemo(
    () => (Array.isArray(stats?.phoneByDay) ? stats.phoneByDay : []),
    [stats]
  );

  const fmtInt = n => Number(n || 0).toLocaleString('de-DE');
  const fmtPct = n => `${percentFormatter.format(Number(n || 0))} %`;
  const fmtSec = n => `${fmtInt(n)} sec`;
  const fmtEuro = n =>
    Number(n || 0).toLocaleString('de-DE', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 2,
    });

  const ccAdded = stats?.ccAdded || null;
  const ccAddedByDay = useMemo(
    () => (Array.isArray(stats?.ccAddedByDay) ? stats.ccAddedByDay : []),
    [stats]
  );
  const ccAddedByUser = useMemo(
    () => (Array.isArray(stats?.ccAddedByUser) ? stats.ccAddedByUser : []),
    [stats]
  );
  const ccAddedByBranch = useMemo(
    () => (Array.isArray(stats?.ccAddedByBranch) ? stats.ccAddedByBranch : []),
    [stats]
  );
  const ccAddedByWeekday = useMemo(
    () => (Array.isArray(stats?.ccAddedByWeekday) ? stats.ccAddedByWeekday : []),
    [stats]
  );
  const ccAddedByHour = useMemo(
    () => (Array.isArray(stats?.ccAddedByHour) ? stats.ccAddedByHour : []),
    [stats]
  );
  const ccAddedChartData = useMemo(
    () =>
      ccAddedByDay.map(p => ({
        ...p,
        label: dayjs(p.date).isValid() ? dayjs(p.date).format('DD.MM.') : String(p.date || ''),
      })),
    [ccAddedByDay]
  );

  const phoneCards = useMemo(() => {
    if (!phoneSummary) return [];
    return [
      {
        label: 'Eingegangene Anrufe',
        value: fmtInt(phoneSummary.callsReceived),
        helper: 'Anrufe mit Abschluss im Zeitraum',
      },
      {
        label: 'Eingegangene Anrufe (unique)',
        value: fmtInt(phoneSummary.callsReceivedUnique),
        helper: 'Unterschiedliche Anrufer',
      },
      {
        label: 'Offene Rückrufe (Tagesende)',
        value: fmtInt(phoneSummary.callbacksOpen),
        helper: 'Status: waitingForCallback',
      },
      {
        label: 'Durchgeführte Rückrufe',
        value: fmtInt(phoneSummary.callbackCount),
        helper: 'Summe ausgehender Rückrufaktionen',
      },
      {
        label: 'Direkt beantwortete Anrufe',
        value: fmtInt(phoneSummary.callsDirectlyAnswered),
        helper: 'successfulOnFirstCall',
      },
      {
        label: 'Anteil direkt beantwortet',
        value: fmtPct(phoneSummary.callsDirectlyAnsweredQuote),
        helper: 'direkt beantwortet / eingegangen',
      },
      {
        label: 'Ø Anrufdauer',
        value: fmtSec(phoneSummary.callsDurationAverage),
        helper: 'Gesprächszeit pro erfolgreich geführtem Anruf',
      },
      {
        label: 'Ø Wartedauer',
        value: fmtSec(phoneSummary.callsWaittimeAverage),
        helper: 'Klingelzeit vor Annahme',
      },
    ];
  }, [phoneSummary]);

  const phoneChartData = useMemo(() => {
    return phoneByDay.map(p => ({
      ...p,
      label: dayjs(p.date).isValid() ? dayjs(p.date).format('DD.MM.') : String(p.date || ''),
    }));
  }, [phoneByDay]);

  const phoneByWeekday = useMemo(
    () => (Array.isArray(stats?.phoneByWeekday) ? stats.phoneByWeekday : []),
    [stats]
  );
  const phoneByHour = useMemo(
    () => (Array.isArray(stats?.phoneByHour) ? stats.phoneByHour : []),
    [stats]
  );
  const phoneByWeekdayHour = useMemo(
    () => (Array.isArray(stats?.phoneByWeekdayHour) ? stats.phoneByWeekdayHour : []),
    [stats]
  );

  // Absolutwert fuer die Heatmap-Intensitaet.
  const heatmapMax = useMemo(() => {
    let max = 0;
    for (const row of phoneByWeekdayHour) {
      for (const h of row.hours || []) {
        if (h.callsReceived > max) max = h.callsReceived;
      }
    }
    return max;
  }, [phoneByWeekdayHour]);

  const heatmapHasData = heatmapMax > 0;

  return (
    <div style={getStatisticsPageRootStyle(theme)}>
    <StatisticViewShell
      startDate={startDate}
      endDate={endDate}
      onStartDateChange={setStartDate}
      onEndDateChange={setEndDate}
      onApply={applyFilters}
      applyDisabled={isFetching}
      applyExtraActions={
        <Button
          variant='outlined'
          onClick={() => setChartType(prev => (prev === 'line' ? 'bar' : 'line'))}
          sx={{ minWidth: 56, minHeight: 56, padding: 0, borderRadius: 2 }}
          title={chartType === 'line' ? 'Balken' : 'Linie'}
        >
          {chartType === 'line' ? '▦' : '〰'}
        </Button>
      }
      status={status}
      isStale={isStale}
      cacheComputedAt={cacheComputedAt}
      cacheExpiresAt={cacheExpiresAt}
      job={job}
      error={error}
      cacheEntries={cacheEntries}
      selectedEntryId={selectedEntryId}
      onSelectEntry={handleSelectEntry}
      entriesLoading={entriesLoading}
      onRefresh={refreshStatistic}
      hasPayload={!!stats}
    >
      {!isFetching && stats && phoneSummary && (
        <Box component='section' sx={{ mb: 3 }}>
          <Typography variant='h6' component='h2' sx={{ fontWeight: 600, mb: 2 }}>
            Anrufvolumen
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
              gap: '1rem',
              mb: 3,
            }}
          >
            {phoneCards.map(card => (
              <Box
                component='article'
                key={`phone-${card.label}`}
                sx={{
                  bgcolor: statSurfaceBg,
                  borderRadius: 2,
                  p: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                  border: 1,
                  borderColor: 'divider',
                }}
              >
                <Typography component='span' variant='body2' sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
                  {card.label}
                </Typography>
                <Typography component='strong' sx={{ fontSize: '1.5rem', fontWeight: 700 }}>
                  {card.value}
                </Typography>
                <Typography component='small' variant='caption' sx={{ color: 'text.secondary' }}>
                  {card.helper}
                </Typography>
              </Box>
            ))}
          </Box>

          {phoneChartData.length > 0 && (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                gap: 2,
                mb: 2,
              }}
            >
              <Paper elevation={3} sx={{ p: 2, bgcolor: statSurfaceBg, height: 300 }}>
                <Typography variant='body2' sx={{ mb: 1, color: 'text.secondary', fontWeight: 500 }}>
                  Anrufvolumen im Zeitverlauf
                </Typography>
                <ResponsiveContainer width='100%' height='85%'>
                  <LineChart data={phoneChartData}>
                    <CartesianGrid stroke={chartGridStroke} />
                    <XAxis dataKey='label' stroke={axisStroke} />
                    <YAxis stroke={axisStroke} allowDecimals={false} />
                    <Tooltip {...chartTooltipProps} />
                    <Legend />
                    <Line
                      type='monotone'
                      dataKey='callsReceived'
                      name='Eingegangen'
                      stroke='#42a5f5'
                      strokeWidth={2}
                      dot={false}
                    />
                    <Line
                      type='monotone'
                      dataKey='callsDirectlyAnswered'
                      name='Direkt beantwortet'
                      stroke='#4caf50'
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Paper>

              <Paper elevation={3} sx={{ p: 2, bgcolor: statSurfaceBg, height: 300 }}>
                <Typography variant='body2' sx={{ mb: 1, color: 'text.secondary', fontWeight: 500 }}>
                  Dauer & Wartezeit (Sekunden)
                </Typography>
                <ResponsiveContainer width='100%' height='85%'>
                  <LineChart data={phoneChartData}>
                    <CartesianGrid stroke={chartGridStroke} />
                    <XAxis dataKey='label' stroke={axisStroke} />
                    <YAxis stroke={axisStroke} allowDecimals={false} />
                    <Tooltip {...chartTooltipProps} />
                    <Legend />
                    <Line
                      type='monotone'
                      dataKey='callsDurationAverage'
                      name='Ø Anrufdauer'
                      stroke='#ff9800'
                      strokeWidth={2}
                      dot={false}
                    />
                    <Line
                      type='monotone'
                      dataKey='callsWaittimeAverage'
                      name='Ø Wartedauer'
                      stroke='#f44336'
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Paper>
            </Box>
          )}

          {phoneByDay.length > 0 && (
            <Accordion
              disableGutters
              elevation={0}
              sx={plainAccordionSx}
              TransitionProps={{ unmountOnExit: true, mountOnEnter: true }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{ fontWeight: 600 }}>Tägliche Werte (Tabelle)</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                <TableContainer>
                  <Table size='small' stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Tag</TableCell>
                        <TableCell align='right'>Eingegangen</TableCell>
                        <TableCell align='right'>Unique</TableCell>
                        <TableCell align='right'>Offene Rückrufe</TableCell>
                        <TableCell align='right'>Rückrufe</TableCell>
                        <TableCell align='right'>Direkt beantw.</TableCell>
                        <TableCell align='right'>Direkt beantw. %</TableCell>
                        <TableCell align='right'>Ø Dauer</TableCell>
                        <TableCell align='right'>Ø Wartezeit</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {phoneByDay.map(row => (
                        <TableRow key={row.date}>
                          <TableCell>{dayjs(row.date).format('dd, DD.MM.YYYY')}</TableCell>
                          <TableCell align='right'>{fmtInt(row.callsReceived)}</TableCell>
                          <TableCell align='right'>{fmtInt(row.callsReceivedUnique)}</TableCell>
                          <TableCell align='right'>{fmtInt(row.callbacksOpen)}</TableCell>
                          <TableCell align='right'>{fmtInt(row.callbackCount)}</TableCell>
                          <TableCell align='right'>
                            {fmtInt(row.callsDirectlyAnswered)}
                          </TableCell>
                          <TableCell align='right'>
                            {fmtPct(row.callsDirectlyAnsweredQuote)}
                          </TableCell>
                          <TableCell align='right'>{fmtSec(row.callsDurationAverage)}</TableCell>
                          <TableCell align='right'>{fmtSec(row.callsWaittimeAverage)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>
          )}

          {(phoneByWeekday.length > 0 || phoneByHour.length > 0) && (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                gap: 2,
                mt: 2,
              }}
            >
              {phoneByWeekday.length > 0 && (
                <Paper elevation={3} sx={{ p: 2, bgcolor: statSurfaceBg, height: 320 }}>
                  <Typography variant='body2' sx={{ mb: 1, color: 'text.secondary', fontWeight: 500 }}>
                    Anrufvolumen nach Wochentag
                  </Typography>
                  <ResponsiveContainer width='100%' height='88%'>
                    <ComposedChart data={phoneByWeekday}>
                      <CartesianGrid stroke={chartGridStroke} vertical={false} />
                      <XAxis dataKey='label' stroke={axisStroke} />
                      <YAxis
                        yAxisId='left'
                        stroke='#42a5f5'
                        allowDecimals={false}
                      />
                      <YAxis
                        yAxisId='right'
                        orientation='right'
                        stroke='#4caf50'
                        tickFormatter={v => `${v}%`}
                        domain={[0, 100]}
                      />
                      <Tooltip
                        cursor={barTooltipCursor}
                        {...chartTooltipProps}
                        formatter={(value, name) =>
                          name === 'Direkt beantwortet %'
                            ? [`${fmtPct(value)}`, name]
                            : [fmtInt(value), name]
                        }
                      />
                      <Legend />
                      <Bar
                        yAxisId='left'
                        dataKey='callsReceived'
                        name='Eingegangen'
                        fill='#42a5f5'
                      />
                      <Line
                        yAxisId='right'
                        type='monotone'
                        dataKey='callsDirectlyAnsweredQuote'
                        name='Direkt beantwortet %'
                        stroke='#4caf50'
                        strokeWidth={2}
                        dot
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </Paper>
              )}

              {phoneByHour.length > 0 && (
                <Paper elevation={3} sx={{ p: 2, bgcolor: statSurfaceBg, height: 320 }}>
                  <Typography variant='body2' sx={{ mb: 1, color: 'text.secondary', fontWeight: 500 }}>
                    Anrufvolumen nach Tageszeit
                  </Typography>
                  <ResponsiveContainer width='100%' height='88%'>
                    <ComposedChart data={phoneByHour}>
                      <CartesianGrid stroke={chartGridStroke} vertical={false} />
                      <XAxis dataKey='label' stroke={axisStroke} interval={1} fontSize={10} />
                      <YAxis
                        yAxisId='left'
                        stroke='#42a5f5'
                        allowDecimals={false}
                      />
                      <YAxis
                        yAxisId='right'
                        orientation='right'
                        stroke='#f44336'
                        tickFormatter={v => `${v}s`}
                      />
                      <Tooltip
                        cursor={barTooltipCursor}
                        {...chartTooltipProps}
                        formatter={(value, name) =>
                          name === 'Ø Wartedauer'
                            ? [fmtSec(value), name]
                            : [fmtInt(value), name]
                        }
                      />
                      <Legend />
                      <Bar
                        yAxisId='left'
                        dataKey='callsReceived'
                        name='Eingegangen'
                        fill='#42a5f5'
                      />
                      <Line
                        yAxisId='right'
                        type='monotone'
                        dataKey='callsWaittimeAverage'
                        name='Ø Wartedauer'
                        stroke='#f44336'
                        strokeWidth={2}
                        dot={false}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </Paper>
              )}
            </Box>
          )}

          {heatmapHasData && (
            <Paper elevation={3} sx={{ p: 2, bgcolor: statSurfaceBg, mt: 2 }}>
              <Typography variant='body2' sx={{ mb: 1, color: 'text.secondary', fontWeight: 500 }}>
                Heatmap: Wochentag × Stunde (eingegangene Anrufe)
              </Typography>
              <Box sx={{ overflowX: 'auto' }}>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '48px repeat(24, minmax(22px, 1fr))',
                    gap: 0.25,
                    minWidth: 24 * 22 + 48,
                  }}
                >
                  <Box />
                  {Array.from({ length: 24 }, (_, h) => (
                    <Box
                      key={`hhead-${h}`}
                      sx={{
                        fontSize: '0.65rem',
                        color: 'text.secondary',
                        textAlign: 'center',
                        lineHeight: 1,
                      }}
                    >
                      {h % 2 === 0 ? String(h).padStart(2, '0') : ''}
                    </Box>
                  ))}
                  {phoneByWeekdayHour.map(row => (
                    <React.Fragment key={`wd-${row.weekday}`}>
                      <Box
                        sx={{
                          fontSize: '0.75rem',
                          color: 'text.secondary',
                          display: 'flex',
                          alignItems: 'center',
                          pr: 0.5,
                        }}
                      >
                        {row.label}
                      </Box>
                      {(row.hours || []).map(h => {
                        const v = h.callsReceived || 0;
                        const intensity = heatmapMax > 0 ? v / heatmapMax : 0;
                        const alpha = v === 0 ? 0.06 : 0.18 + intensity * 0.82;
                        return (
                          <Box
                            key={`wd-${row.weekday}-h-${h.hour}`}
                            title={`${row.label} ${String(h.hour).padStart(2, '0')}:00 – ${fmtInt(v)} Anrufe, Ø Warten ${fmtSec(h.callsWaittimeAverage)}`}
                            sx={{
                              height: 22,
                              borderRadius: 0.5,
                              bgcolor: `rgba(66, 165, 245, ${alpha})`,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '0.6rem',
                              color:
                                intensity > 0.55
                                  ? theme.palette.common.white
                                  : theme.palette.mode === 'light'
                                    ? theme.palette.text.secondary
                                    : theme.palette.grey[400],
                            }}
                          >
                            {v > 0 && intensity > 0.35 ? v : ''}
                          </Box>
                        );
                      })}
                    </React.Fragment>
                  ))}
                </Box>
              </Box>
              <Typography variant='caption' sx={{ color: 'text.secondary', display: 'block', mt: 1 }}>
                Farbintensität = eingegangene Anrufe im Slot. Max: {fmtInt(heatmapMax)}.
              </Typography>
            </Paper>
          )}

          <Box sx={{ mt: 2 }}>
            <StatisticMatrixTables
              title='Tabellen pro Kennzahl (wie alte Buchungsstatistik)'
              matrix={stats?.phoneMatrix}
              metrics={CALL_VOLUME_METRICS}
            />
          </Box>
        </Box>
      )}

      {!isFetching && stats && ccAdded && (
        <Box component='section' sx={{ mb: 3 }}>
          <Typography variant='h6' component='h2' sx={{ fontWeight: 600, mb: 2 }}>
            Einbuchungen durch Call Center
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
              gap: '1rem',
              mb: 3,
            }}
          >
            {[
              {
                label: 'Neue Buchungen',
                value: fmtInt(ccAdded.count),
                helper: 'meta.createdBy = CC-Mitarbeiter',
              },
              {
                label: 'Gesamtvolumen',
                value: fmtEuro(ccAdded.total),
                helper: 'Summe totalAmount',
              },
              {
                label: 'Ø Buchungswert',
                value: fmtEuro(ccAdded.averageValue),
                helper: 'Gesamt / Anzahl',
              },
              {
                label: 'Aktive CC-Einbucher',
                value: fmtInt(ccAddedByUser.length),
                helper: 'Unterschiedliche Mitarbeiter',
              },
            ].map(card => (
              <Box
                component='article'
                key={`cc-added-${card.label}`}
                sx={{
                  bgcolor: statSurfaceBg,
                  borderRadius: 2,
                  p: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                  border: 1,
                  borderColor: 'divider',
                }}
              >
                <Typography component='span' variant='body2' sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
                  {card.label}
                </Typography>
                <Typography component='strong' sx={{ fontSize: '1.5rem', fontWeight: 700 }}>
                  {card.value}
                </Typography>
                <Typography component='small' variant='caption' sx={{ color: 'text.secondary' }}>
                  {card.helper}
                </Typography>
              </Box>
            ))}
          </Box>

          {ccAddedChartData.length > 0 && ccAdded.count > 0 && (
            <Paper elevation={3} sx={{ p: 2, bgcolor: statSurfaceBg, height: 320, mb: 2 }}>
              <Typography variant='body2' sx={{ mb: 1, color: 'text.secondary', fontWeight: 500 }}>
                Einbuchungen im Zeitverlauf
              </Typography>
              <ResponsiveContainer width='100%' height='88%'>
                <ComposedChart data={ccAddedChartData}>
                  <CartesianGrid stroke={chartGridStroke} />
                  <XAxis dataKey='label' stroke={axisStroke} />
                  <YAxis
                    yAxisId='left'
                    stroke='#4caf50'
                    allowDecimals={false}
                  />
                  <YAxis
                    yAxisId='right'
                    orientation='right'
                    stroke='#ff9800'
                    tickFormatter={v =>
                      new Intl.NumberFormat('de-DE', {
                        notation: 'compact',
                        style: 'currency',
                        currency: 'EUR',
                        maximumFractionDigits: 1,
                      }).format(Number(v || 0))
                    }
                  />
                  <Tooltip
                    {...chartTooltipProps}
                    formatter={(value, name) =>
                      name === 'Volumen' ? [fmtEuro(value), name] : [fmtInt(value), name]
                    }
                  />
                  <Legend />
                  <Bar
                    yAxisId='left'
                    dataKey='count'
                    name='Anzahl'
                    fill='#4caf50'
                  />
                  <Line
                    yAxisId='right'
                    type='monotone'
                    dataKey='total'
                    name='Volumen'
                    stroke='#ff9800'
                    strokeWidth={2}
                    dot={false}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </Paper>
          )}

          {(ccAddedByWeekday.length > 0 || ccAddedByHour.length > 0) && ccAdded.count > 0 && (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                gap: 2,
                mb: 2,
              }}
            >
              {ccAddedByWeekday.length > 0 && (
                <Paper elevation={3} sx={{ p: 2, bgcolor: statSurfaceBg, height: 280 }}>
                  <Typography variant='body2' sx={{ mb: 1, color: 'text.secondary', fontWeight: 500 }}>
                    Einbuchungen nach Wochentag
                  </Typography>
                  <ResponsiveContainer width='100%' height='88%'>
                    <BarChart data={ccAddedByWeekday}>
                      <CartesianGrid stroke={chartGridStroke} vertical={false} />
                      <XAxis dataKey='label' stroke={axisStroke} />
                      <YAxis stroke={axisStroke} allowDecimals={false} />
                      <Tooltip
                        cursor={barTooltipCursor}
                        {...chartTooltipProps}
                        formatter={(value, name) =>
                          name === 'Volumen' ? [fmtEuro(value), name] : [fmtInt(value), name]
                        }
                      />
                      <Legend />
                      <Bar dataKey='count' name='Anzahl' fill='#4caf50' />
                    </BarChart>
                  </ResponsiveContainer>
                </Paper>
              )}

              {ccAddedByHour.length > 0 && (
                <Paper elevation={3} sx={{ p: 2, bgcolor: statSurfaceBg, height: 280 }}>
                  <Typography variant='body2' sx={{ mb: 1, color: 'text.secondary', fontWeight: 500 }}>
                    Einbuchungen nach Tageszeit
                  </Typography>
                  <ResponsiveContainer width='100%' height='88%'>
                    <BarChart data={ccAddedByHour}>
                      <CartesianGrid stroke={chartGridStroke} vertical={false} />
                      <XAxis dataKey='label' stroke={axisStroke} interval={1} fontSize={10} />
                      <YAxis stroke={axisStroke} allowDecimals={false} />
                      <Tooltip
                        cursor={barTooltipCursor}
                        {...chartTooltipProps}
                        formatter={(value, name) =>
                          name === 'Volumen' ? [fmtEuro(value), name] : [fmtInt(value), name]
                        }
                      />
                      <Legend />
                      <Bar dataKey='count' name='Anzahl' fill='#4caf50' />
                    </BarChart>
                  </ResponsiveContainer>
                </Paper>
              )}
            </Box>
          )}

          {ccAddedByUser.length > 0 && (
            <Accordion
              disableGutters
              elevation={0}
              sx={{ ...plainAccordionSx, mb: 1 }}
              TransitionProps={{ unmountOnExit: true, mountOnEnter: true }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{ fontWeight: 600 }}>
                  Einbuchungen pro CC-Mitarbeiter ({ccAddedByUser.length})
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                <TableContainer>
                  <Table size='small' stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Mitarbeiter</TableCell>
                        <TableCell>Rollen</TableCell>
                        <TableCell align='right'>Anzahl</TableCell>
                        <TableCell align='right'>Volumen</TableCell>
                        <TableCell align='right'>Ø Buchungswert</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {ccAddedByUser.map(u => (
                        <TableRow key={u.userId}>
                          <TableCell>
                            <div>
                              <strong>{u.displayName}</strong>
                              <Box component='div' sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                                @{u.username}
                              </Box>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Stack direction='row' spacing={0.5} flexWrap='wrap' useFlexGap>
                              {(u.roles || []).map(role => (
                                <Chip
                                  key={`${u.userId}-${role}`}
                                  label={role === 'ccManager' ? 'Manager' : 'Mitarbeiter'}
                                  size='small'
                                  color={role === 'ccManager' ? 'primary' : 'default'}
                                  sx={{ fontSize: '0.7rem' }}
                                />
                              ))}
                            </Stack>
                          </TableCell>
                          <TableCell align='right'>{fmtInt(u.count)}</TableCell>
                          <TableCell align='right'>{fmtEuro(u.total)}</TableCell>
                          <TableCell align='right'>
                            {u.count > 0 ? fmtEuro(u.total / u.count) : '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>
          )}

          {ccAddedByBranch.length > 0 && (
            <Accordion
              disableGutters
              elevation={0}
              sx={plainAccordionSx}
              TransitionProps={{ unmountOnExit: true, mountOnEnter: true }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{ fontWeight: 600 }}>
                  Einbuchungen pro Standort ({ccAddedByBranch.length})
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                <TableContainer>
                  <Table size='small' stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Standort</TableCell>
                        <TableCell align='right'>Anzahl</TableCell>
                        <TableCell align='right'>Volumen</TableCell>
                        <TableCell align='right'>Ø Buchungswert</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {ccAddedByBranch.map(b => (
                        <TableRow key={b.branchId}>
                          <TableCell>{b.branchName}</TableCell>
                          <TableCell align='right'>{fmtInt(b.count)}</TableCell>
                          <TableCell align='right'>{fmtEuro(b.total)}</TableCell>
                          <TableCell align='right'>
                            {b.count > 0 ? fmtEuro(b.total / b.count) : '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>
          )}

          {ccAdded.count === 0 && (
            <Typography sx={{ color: 'text.secondary' }}>
              Im gewählten Zeitraum wurden keine Einbuchungen durch Call-Center-Mitarbeiter erfasst.
            </Typography>
          )}
        </Box>
      )}

      <section>
        <Typography variant='h6' component='h2' sx={{ mb: 1 }}>
          Kennzahlen
        </Typography>
        {stats && (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
              gap: '1rem',
            }}
          >
            {cards.map(card => (
              <article
                key={card.label}
                style={{
                  backgroundColor: statSurfaceBg,
                  borderRadius: '8px',
                  padding: '1rem',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.25rem',
                  border: `1px solid ${theme.palette.divider}`,
                }}
              >
                <span style={{ fontSize: '0.85rem', color: kpiLabelColor }}>{card.label}</span>
                <strong style={{ fontSize: '1.5rem' }}>{card.value}</strong>
                <small style={{ color: kpiHelperColor }}>{card.helper}</small>
              </article>
            ))}
          </div>
        )}
      </section>

      {stats && (
        <section>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            alignItems={{ xs: 'flex-start', md: 'center' }}
            justifyContent='space-between'
            sx={{ mb: 1 }}
          >
            <Typography variant='h6' component='h2' sx={{ m: 0 }}>
              Call Center Aktivität
            </Typography>
          </Stack>
          <Paper elevation={3} sx={{ p: 2, bgcolor: statSurfaceBg, height: 360 }}>
            <ResponsiveContainer width='100%' height='100%'>
              {chartType === 'line' ? (
                <LineChart data={chartData}>
                  <CartesianGrid stroke={chartGridStroke} />
                  <XAxis dataKey='label' stroke={axisStroke} />
                  <YAxis stroke={axisStroke} allowDecimals={false} />
                  <Tooltip {...chartTooltipProps} />
                  <Legend {...chartLegendBarProps} />
                  <Line
                    type='monotone'
                    dataKey='changeCount'
                    name='Änderungen'
                    stroke={activityLineColors.changes}
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 4 }}
                  />
                  <Line
                    type='monotone'
                    dataKey='bookingCount'
                    name='Bearbeitete Buchungen'
                    stroke={activityLineColors.bookings}
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 4 }}
                  />
                </LineChart>
              ) : (
                <BarChart data={chartData}>
                  <CartesianGrid stroke={chartGridStroke} vertical={false} />
                  <XAxis dataKey='label' stroke={axisStroke} />
                  <YAxis stroke={axisStroke} allowDecimals={false} />
                  <Tooltip cursor={barTooltipCursor} {...chartTooltipProps} />
                  <Legend {...chartLegendBarProps} />
                  <Bar dataKey='changeCount' name='Änderungen' fill={activityLineColors.changes} />
                  <Bar dataKey='bookingCount' name='Bearbeitete Buchungen' fill={activityLineColors.bookings} />
                </BarChart>
              )}
            </ResponsiveContainer>
          </Paper>
        </section>
      )}

      {stats && hourlyData.length > 0 && (
        <section>
          <Typography variant='h6' component='h2' sx={{ mb: 2 }}>
            Änderungsaktivität nach Tageszeit
          </Typography>
          <Paper elevation={3} sx={{ p: 2, bgcolor: statSurfaceBg, height: 300 }}>
            <ResponsiveContainer width='100%' height='100%'>
              <BarChart data={hourlyData}>
                <CartesianGrid stroke={chartGridStroke} vertical={false} />
                <XAxis dataKey='label' stroke={axisStroke} interval={1} fontSize={10} />
                <YAxis stroke={axisStroke} allowDecimals={false} />
                <Tooltip cursor={barTooltipCursor} {...chartTooltipProps} />
                <Bar dataKey='count' name='Änderungen' fill={activityLineColors.hourly} />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </section>
      )}

      {stats && (changeTypePieData.length > 0 || userPieData.length > 0) && (
        <section>
          <Typography variant='h6' component='h2' sx={{ mb: 2 }}>
            Verteilung
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              alignItems: 'stretch',
            }}
          >
            {changeTypePieData.length > 0 && (
              <Paper
                elevation={3}
                sx={{
                  p: 3,
                  bgcolor: statSurfaceBg,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Typography variant='subtitle1' component='h3' sx={{ mt: 0, mb: 1.5, fontWeight: 600 }}>
                  Art der Änderungen
                </Typography>
                <div style={{ width: '100%', height: 260 }}>
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie
                        data={changeTypePieData}
                        cx='50%'
                        cy='50%'
                        innerRadius={60}
                        outerRadius={90}
                        paddingAngle={2}
                        dataKey='value'
                        stroke='none'
                      >
                        {changeTypePieData.map((entry, idx) => (
                          <Cell
                            key={`${entry.name}-${idx}`}
                            fill={chartPaletteColors[idx % chartPaletteColors.length]}
                          />
                        ))}
                      </Pie>
                      <Tooltip formatter={value => String(value)} {...chartTooltipProps} />
                      <Legend {...chartLegendPieProps} verticalAlign='bottom' height={48} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </Paper>
            )}

            {userPieData.length > 0 && (
              <Paper
                elevation={3}
                sx={{
                  p: 3,
                  bgcolor: statSurfaceBg,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Typography variant='subtitle1' component='h3' sx={{ mt: 0, mb: 1.5, fontWeight: 600 }}>
                  Änderungen pro Mitarbeiter
                </Typography>
                <div style={{ width: '100%', height: 260 }}>
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie
                        data={userPieData}
                        cx='50%'
                        cy='50%'
                        innerRadius={60}
                        outerRadius={90}
                        paddingAngle={2}
                        dataKey='value'
                        stroke='none'
                      >
                        {userPieData.map((entry, idx) => (
                          <Cell
                            key={`${entry.name}-${idx}`}
                            fill={chartPaletteColors[idx % chartPaletteColors.length]}
                          />
                        ))}
                      </Pie>
                      <Tooltip formatter={value => String(value)} {...chartTooltipProps} />
                      <Legend {...chartLegendPieProps} verticalAlign='bottom' height={48} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </Paper>
            )}
          </Box>
        </section>
      )}

      {stats && stats.userStats && stats.userStats.length > 0 && (
        <section>
          <Typography variant='h6' component='h2' sx={{ mb: 2 }}>
            Mitarbeiter-Leistung
          </Typography>
          <Paper
            elevation={3}
            sx={{
              p: 3,
              bgcolor: statSurfaceBg,
            }}
          >
            <div style={{ overflowX: 'auto' }}>
              <table
                style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  minWidth: '640px',
                }}
              >
                <thead>
                  <tr>
                    <th style={htmlThStyle()}>Mitarbeiter</th>
                    <th style={htmlThStyle()}>Rollen</th>
                    <th style={htmlThStyle({ textAlign: 'right' })}>Änderungen</th>
                    <th style={htmlThStyle({ textAlign: 'right' })}>Buchungen bearbeitet</th>
                    <th style={htmlThStyle({ textAlign: 'right' })}>Ø Änderungen/Buchung</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.userStats.map(user => (
                    <tr key={user.userId}>
                      <td style={{ padding: '0.75rem', borderTop: tableRowBorderTop }}>
                        <div>
                          <strong>{user.displayName}</strong>
                          <div style={{ fontSize: '0.8rem', color: kpiHelperColor }}>@{user.username}</div>
                        </div>
                      </td>
                      <td style={{ padding: '0.75rem', borderTop: tableRowBorderTop }}>
                        <Stack direction='row' spacing={0.5} flexWrap='wrap' useFlexGap>
                          {user.roles.map(role => (
                            <Chip
                              key={role}
                              label={role === 'ccManager' ? 'Manager' : 'Mitarbeiter'}
                              size='small'
                              color={role === 'ccManager' ? 'primary' : 'default'}
                              sx={{ fontSize: '0.7rem' }}
                            />
                          ))}
                        </Stack>
                      </td>
                      <td
                        style={{
                          padding: '0.75rem',
                          borderTop: tableRowBorderTop,
                          textAlign: 'right',
                        }}
                      >
                        <strong>{user.changeCount}</strong>
                      </td>
                      <td
                        style={{
                          padding: '0.75rem',
                          borderTop: tableRowBorderTop,
                          textAlign: 'right',
                        }}
                      >
                        {user.bookingsModified}
                      </td>
                      <td
                        style={{
                          padding: '0.75rem',
                          borderTop: tableRowBorderTop,
                          textAlign: 'right',
                        }}
                      >
                        {user.bookingsModified > 0
                          ? (user.changeCount / user.bookingsModified).toFixed(1)
                          : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Paper>
        </section>
      )}

      {stats && stats.changeTypes && stats.changeTypes.length > 0 && (
        <section>
          <Typography variant='h6' component='h2' sx={{ mb: 2 }}>
            Änderungstypen
          </Typography>
          <Paper
            elevation={3}
            sx={{
              p: 3,
              bgcolor: statSurfaceBg,
            }}
          >
            <div style={{ overflowX: 'auto' }}>
              <table
                style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  minWidth: '400px',
                }}
              >
                <thead>
                  <tr>
                    <th style={htmlThStyle()}>Änderungstyp</th>
                    <th style={htmlThStyle({ textAlign: 'right' })}>Anzahl</th>
                    <th style={htmlThStyle({ textAlign: 'right' })}>Anteil</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.changeTypes.map(type => (
                    <tr key={type.key}>
                      <td style={{ padding: '0.75rem', borderTop: tableRowBorderTop }}>
                        {type.label}
                      </td>
                      <td
                        style={{
                          padding: '0.75rem',
                          borderTop: tableRowBorderTop,
                          textAlign: 'right',
                        }}
                      >
                        {type.count}
                      </td>
                      <td
                        style={{
                          padding: '0.75rem',
                          borderTop: tableRowBorderTop,
                          textAlign: 'right',
                        }}
                      >
                        {stats.counts.totalCCChanges > 0
                          ? percentFormatter.format((type.count / stats.counts.totalCCChanges) * 100)
                          : 0}{' '}
                        %
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Paper>
        </section>
      )}
    </StatisticViewShell>
    </div>
  );
};

export default CallCenterStatistics;
